/* eslint no-console: 0, no-underscore-dangle: 0 */
import { isSodimacStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import constants from '../../../config/constants';
import { icons } from '../../../config/styles';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import _ from '../../../utils/LodashImports';
import { noop } from '../../../utils/noop';
import { mapATGPricesToCatalystPrices } from '../../../utils/PriceHelpers';
import { recoAnalytics } from '../../../utils/recoAnalytics';
import Heading from '../Heading/Heading';
import { MobileCarouselStyles } from './MobileCarousel.style';
import {
  ANALYTICS_CONSTANTS,
  calculateIndicatorCount,
  calculateSlideIndex,
  getScrollMultiplicant,
  processProducts,
  shouldAttachScrollEvent,
  shouldShowIndicators,
  shouldShowNavigationArrows,
} from './MobileCarousel.utils';
import MobileCarouselPod from './MobileCarouselPod/MobileCarouselPod';

const MobileCarousel = ({
  products,
  appCtx,
  heading,
  subHeading,
  carouselPosition,
  parentVariantID,
  handleViewedProducts,
  isSponsored,
  sponsoredClick,
  slotId,
  carouselId,
}) => {
  const uniqueViewedProducts = new Set();
  const { regionCode, deviceType } = appCtx;
  const toggles = _.get(appCtx, 'siteConfig.toggles', {});
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const processedProducts = processProducts(products);
  const mProducts = processedProducts.map((product) => ({
    ...product,
    prices: mapATGPricesToCatalystPrices(product.prices, regionCode, toggles),
  }));
  const adTypeCarousel =
    subHeading === constants.SPONSORED_PRODUCTS_CAROUSEL_SUB_HEADING
      ? ANALYTICS_CONSTANTS.SPONSORED_CAROUSEL_AD_TYPE
      : ANALYTICS_CONSTANTS.HYBRID_CAROUSEL_AD_TYPE;

  const aggregateViewedProducts = (id) => {
    if (isSponsored && uniqueViewedProducts.has(id)) return;
    uniqueViewedProducts.add(id);

    if (isSodimacStandalone(appCtx.store) && !isSponsored) {
      recoAnalytics.impression({
        carouselName: heading,
        skusViewed: [id],
      });
      return;
    }

    const payloadTitle = heading.split(' ').join('_');
    const skus = Array.from(uniqueViewedProducts).join(',');
    handleViewedProducts(payloadTitle, skus);
  };

  /**
   * Mobile/Tablet scroll event handler
   * Attaches scroll listener only for non-desktop devices
   */
  useEffect(() => {
    const carouselElement = containerRef.current;

    if (!carouselElement || !shouldAttachScrollEvent(deviceType)) {
      return undefined;
    }

    const scrollEvent = () => {
      const { scrollLeft, clientWidth } = carouselElement;
      const multiplicant = getScrollMultiplicant(deviceType);
      const currentIndex = calculateSlideIndex(scrollLeft, clientWidth, multiplicant);
      setSlideIndex(currentIndex);
    };

    carouselElement.addEventListener('scroll', scrollEvent);

    return () => {
      carouselElement.removeEventListener('scroll', scrollEvent);
    };
  }, [deviceType]);

  const handleNextSlideClick = () => {
    if (!containerRef.current || deviceType !== 'desktop') {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const podWidth = (containerWidth - 80) / 6;
    const slideWidth = 6 * podWidth + 80;
    const currentSlide = slideIndex;
    const maxSlides = Math.ceil(mProducts.length / 6);

    if (currentSlide >= maxSlides - 1) {
      setCanScrollNext(false);
      return;
    }

    const nextSlidePosition = (currentSlide + 1) * slideWidth;
    containerRef.current.scrollTo({
      left: nextSlidePosition,
      behavior: 'smooth',
    });

    setSlideIndex(currentSlide + 1);
    setCanScrollPrev(true);
    if (currentSlide + 1 >= maxSlides - 1) {
      setCanScrollNext(false);
    }
  };

  const handlePreviousSlideClick = () => {
    if (!containerRef.current || deviceType !== 'desktop') {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const podWidth = (containerWidth - 80) / 6;
    const slideWidth = 6 * podWidth + 80;
    const currentSlide = slideIndex;

    if (currentSlide === 0) {
      setCanScrollPrev(false);
      return;
    }

    const prevSlidePosition = (currentSlide - 1) * slideWidth;
    containerRef.current.scrollTo({
      left: prevSlidePosition,
      behavior: 'smooth',
    });

    setSlideIndex(currentSlide - 1);
    setCanScrollNext(true);
    if (currentSlide - 1 === 0) {
      setCanScrollPrev(false);
    }
  };

  const handleIndicatorClick = (index) => {
    if (!containerRef.current) {
      return;
    }

    if (deviceType === 'desktop') {
      const containerWidth = containerRef.current.clientWidth;
      const podWidth = (containerWidth - 80) / 6;
      const slideWidth = 6 * podWidth + 80;
      const scrollPosition = index * slideWidth;

      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });

      const maxSlides = Math.ceil(mProducts.length / 6);
      setCanScrollPrev(index > 0);
      setCanScrollNext(index < maxSlides - 1);
    } else {
      const { clientWidth } = containerRef.current;
      const itemWidth = clientWidth * 0.6;
      const scrollPosition = index * itemWidth;
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }

    setSlideIndex(index);
  };

  const indicatorCount = calculateIndicatorCount(deviceType, mProducts.length);
  const showIndicators = shouldShowIndicators(indicatorCount, deviceType);
  const showNavigationArrows = shouldShowNavigationArrows(deviceType, indicatorCount);

  useEffect(() => {
    if (deviceType === 'desktop' && indicatorCount > 1) {
      const maxSlides = Math.ceil(mProducts.length / 6);
      setCanScrollPrev(slideIndex > 0);
      setCanScrollNext(slideIndex < maxSlides - 1);
    }
  }, [slideIndex, deviceType, indicatorCount, mProducts.length]);

  const showRecommendationsWrapper = mProducts.length > 0;

  return (
    showRecommendationsWrapper && (
      <div data-widget-name={heading} ref={carouselRef} className="mobile-carousel">
        <div className="mobile-carousel__header">
          <Heading variation="title15" level="2">
            {heading}
          </Heading>
          {subHeading && <h2 className="mobile-carousel__subheading">{subHeading}</h2>}
        </div>

        <div className={`mobile-carousel__content mobile-carousel__content--${deviceType}`}>
          {showNavigationArrows && (
            <>
              <button
                className="mobile-carousel__nav mobile-carousel__nav--prev"
                onClick={handlePreviousSlideClick}
                disabled={!canScrollPrev}
                aria-label="Previous products"
                type="button"
              >
                <span dangerouslySetInnerHTML={{ __html: icons.chevronLeftRebranded }} />
              </button>
              <button
                className="mobile-carousel__nav mobile-carousel__nav--next"
                onClick={handleNextSlideClick}
                disabled={!canScrollNext}
                aria-label="Next products"
                type="button"
              >
                <span dangerouslySetInnerHTML={{ __html: icons.chevronRightRebranded }} />
              </button>
            </>
          )}

          <div className="mobile-carousel__track" ref={containerRef}>
            {mProducts.map((item, itemIndex) => (
              <MobileCarouselPod
                key={item.productId}
                product={item}
                appCtx={appCtx}
                aggregateViewedProducts={aggregateViewedProducts}
                details={{
                  numberProducts: mProducts.length,
                  productPosition: itemIndex + 1,
                  carouselPosition,
                  carouselName: heading,
                  parentVariantID,
                  slotId,
                  adType: adTypeCarousel,
                  carouselId,
                }}
                isSponsored={isSponsored}
                sponsoredClick={sponsoredClick}
              />
            ))}
          </div>
        </div>

        {showIndicators && (
          <div className="mobile-carousel__indicators">
            {Array.from({ length: indicatorCount }, (unused, indicatorIndex) => (
              <button
                key={`${heading}-indicator-${indicatorIndex}`}
                className={`mobile-carousel__dot ${
                  slideIndex === indicatorIndex ? 'mobile-carousel__dot--active' : ''
                }`}
                onClick={() => handleIndicatorClick(indicatorIndex)}
                aria-label={`Go to slide ${indicatorIndex + 1}`}
                type="button"
              />
            ))}
          </div>
        )}

        <style jsx>{MobileCarouselStyles}</style>
      </div>
    )
  );
};

MobileCarousel.defaultProps = {
  handleViewedProducts: noop,
  isSponsored: false,
  sponsoredClick: noop,
  slotId: '',
  subHeading: '',
  carouselId: '',
};

MobileCarousel.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  appCtx: PropTypes.object.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string,
  carouselPosition: PropTypes.string.isRequired,
  parentVariantID: PropTypes.number.isRequired,
  handleViewedProducts: PropTypes.func,
  isSponsored: PropTypes.bool,
  sponsoredClick: PropTypes.func,
  slotId: PropTypes.string,
  carouselId: PropTypes.string,
};

export default withApplicationContext(MobileCarousel);
export { MobileCarousel };
